/**
 * Deterministic color hash for relationship overlay lines.
 *
 * Same `relationKey` always maps to the same hue. Hues are spread using the
 * golden-ratio conjugate so that the first ~10 distinct keys are visually
 * well-separated even before any clustering. Saturation/lightness are fixed
 * so colors share a consistent character.
 */

const GOLDEN_RATIO_CONJUGATE = 0.618033988749895

/**
 * Stable 32-bit-ish hash of a string. Produces a non-negative number.
 * Uses the classic `(h << 5) - h + c` recurrence so the result is
 * platform-independent and reproducible across browsers / Node.
 */
export function hueFromString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0
  }
  // Multiply by golden-ratio conjugate to spread small clusters of similar
  // strings (e.g. linkedCctv vs linkedCctv2) across the hue circle.
  return ((Math.abs(h) * GOLDEN_RATIO_CONJUGATE) % 1) * 360
}

/**
 * Deterministic HSL color string for a relationship key.
 * Always returns a valid CSS color even for empty / weird inputs.
 */
export function colorForRelationKey(key: string): string {
  const h = hueFromString(key)
  return `hsl(${h.toFixed(1)} 70% 55%)`
}
